
/**
 * EditableSourceGrid — the editable "Source" view that lives in the mapper's
 * bottom drawer. Extracted from the old standalone `WorkbookGridEditor` so the
 * separate `/edit` page could be removed and source editing unified into the
 * mapper.
 *
 * DATA-SAFETY: `saveWorkbookSheet` REPLACES the whole stored sheet with what we
 * pass. The mapper's read-only `SourceGrid` is fed a *small preview sample*, so
 * it must NOT be made saveable directly. This component is therefore fed the
 * FULL sheet (loaded by the page with `getWorkbookSheet({ limit, offset:0 })`).
 * If the sheet is larger than the loaded slice (`initialTotal > loadedCount`),
 * Save is DISABLED and an explainer is shown — saving a partial view would
 * delete every row beyond the slice. Large sheets must be edited via re-upload.
 */

import {
  FileUp,
  LoaderCircle,
  Plus,
  Save,
  Trash2,
} from "lucide-react";
import { useRef, useState } from "react";
import { saveWorkbookSheet } from "@/lib/api/endpoints";
import type { WorkbookSheetCell } from "@/lib/api/types";

const MIN_VISIBLE_ROWS = 12;
const MIN_VISIBLE_COLUMNS = 4;

type Props = {
  workbookId: string;
  sheetName: string;
  initialHeaders: string[];
  initialRows: WorkbookSheetCell[][];
  initialTotal: number;
  /** Called after a successful save (e.g. to re-run the mapper preview). */
  onSaved?: () => void;
  /** Click a header to insert a source token into the focused field editor. */
  onInsertColumn?: (token: string) => void;
};

type SaveState = "idle" | "saving" | "saved" | "error";

function defaultHeaders(count = MIN_VISIBLE_COLUMNS): string[] {
  return Array.from({ length: count }, (_, index) => `Column ${index + 1}`);
}

function normalizeHeaders(headers: string[]): string[] {
  const source = headers.length > 0 ? headers : defaultHeaders();
  const width = Math.max(source.length, MIN_VISIBLE_COLUMNS);
  return Array.from(
    { length: width },
    (_, index) => source[index] ?? `Column ${index + 1}`,
  );
}

function normalizeRows(
  rows: WorkbookSheetCell[][],
  columnCount: number,
): WorkbookSheetCell[][] {
  const visibleRows = Math.max(rows.length, MIN_VISIBLE_ROWS);
  return Array.from({ length: visibleRows }, (_, rowIndex) => {
    const row = rows[rowIndex] ?? [];
    return Array.from({ length: columnCount }, (_, columnIndex) => {
      const value = row[columnIndex];
      return value === undefined ? null : value;
    });
  });
}

function columnName(index: number): string {
  let n = index + 1;
  let name = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    name = String.fromCharCode(65 + rem) + name;
    n = Math.floor((n - 1) / 26);
  }
  return name;
}

function cellInputValue(value: WorkbookSheetCell): string {
  return value === null ? "" : String(value);
}

function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;

  for (let index = 0; index < text.length; index++) {
    const char = text[index];
    const next = text[index + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        cell += '"';
        index++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      row.push(cell);
      cell = "";
      continue;
    }

    if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && next === "\n") index++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
      continue;
    }

    cell += char;
  }

  row.push(cell);
  rows.push(row);

  return rows.filter((candidate) =>
    candidate.some((value) => value.trim().length > 0),
  );
}

function rowsForSave(rows: WorkbookSheetCell[][], columnCount: number) {
  return rows.map((row) =>
    Array.from({ length: columnCount }, (_, index) => row[index] ?? null),
  );
}

export function EditableSourceGrid({
  workbookId,
  sheetName,
  initialHeaders,
  initialRows,
  initialTotal,
  onSaved,
  onInsertColumn,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [headers, setHeaders] = useState(() => normalizeHeaders(initialHeaders));
  const [rows, setRows] = useState(() =>
    normalizeRows(initialRows, normalizeHeaders(initialHeaders).length),
  );
  // How many real rows were loaded into this editor. If the stored sheet has
  // more rows than this, saving would truncate — so Save is gated.
  const [loadedCount] = useState(initialRows.length);
  const [total, setTotal] = useState(initialTotal);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [message, setMessage] = useState<string | null>(null);

  // Truncation guard: the stored sheet is larger than what we loaded, so a save
  // would delete the rows beyond the loaded slice. CSV import replaces the whole
  // dataset, so once the user imports a CSV we trust their explicit intent.
  const [importedFullDataset, setImportedFullDataset] = useState(false);
  const wouldTruncate = !importedFullDataset && total > loadedCount;

  function setHeader(columnIndex: number, value: string) {
    setHeaders((current) =>
      current.map((header, index) => (index === columnIndex ? value : header)),
    );
  }

  function setCell(rowIndex: number, columnIndex: number, value: string) {
    setRows((current) =>
      current.map((row, rIndex) =>
        rIndex === rowIndex
          ? row.map((cell, cIndex) => (cIndex === columnIndex ? value : cell))
          : row,
      ),
    );
  }

  function addRow() {
    setRows((current) => [
      ...current,
      Array.from({ length: headers.length }, () => null),
    ]);
  }

  function addColumn() {
    setHeaders((current) => [...current, `Column ${current.length + 1}`]);
    setRows((current) => current.map((row) => [...row, null]));
  }

  function deleteRow(rowIndex: number) {
    setRows((current) => {
      const next = current.filter((_, index) => index !== rowIndex);
      return next.length > 0
        ? next
        : [Array.from({ length: headers.length }, () => null)];
    });
  }

  function deleteColumn(columnIndex: number) {
    if (headers.length <= 1) return;
    setHeaders((current) =>
      current.filter((_, index) => index !== columnIndex),
    );
    setRows((current) =>
      current.map((row) => row.filter((_, index) => index !== columnIndex)),
    );
  }

  async function handleCsvFile(file: File) {
    setMessage(null);
    const text = await file.text();
    const parsed = parseCsv(text);
    if (parsed.length === 0) {
      setMessage("CSV did not contain any rows.");
      return;
    }

    const nextHeaders = normalizeHeaders(parsed[0]);
    const nextRows = parsed.slice(1).map((row) =>
      Array.from({ length: nextHeaders.length }, (_, index) => row[index] ?? null),
    );
    setHeaders(nextHeaders);
    setRows(normalizeRows(nextRows, nextHeaders.length));
    // A CSV import replaces the entire dataset, so the truncation guard no
    // longer applies — what's on screen IS the full sheet to be written.
    setImportedFullDataset(true);
    setTotal(parsed.slice(1).length);
    setSaveState("idle");
    setMessage(`${file.name} imported — saving will replace the whole sheet.`);
  }

  async function handleSave() {
    if (wouldTruncate) return;
    setSaveState("saving");
    setMessage(null);

    try {
      const response = await saveWorkbookSheet(workbookId, sheetName, {
        headers,
        rows: rowsForSave(rows, headers.length),
      });
      setHeaders(normalizeHeaders(response.headers));
      setRows(
        normalizeRows(
          response.rows.map((row) =>
            response.headers.map((header) => {
              const value = row[header];
              if (value === null || value === undefined) return null;
              if (typeof value === "number" || typeof value === "string") {
                return value;
              }
              if (typeof value === "boolean") return value ? "TRUE" : "FALSE";
              return String(value);
            }),
          ),
          response.headers.length,
        ),
      );
      setTotal(response.total);
      setImportedFullDataset(false);
      setSaveState("saved");
      setMessage("Saved.");
      onSaved?.();
    } catch (err) {
      setSaveState("error");
      setMessage(err instanceof Error ? err.message : "Save failed.");
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-white dark:bg-surface">
      {/* Toolbar */}
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-1.5 dark:border-border dark:bg-surface-2">
        <span className="text-[11px] text-slate-500 dark:text-slate-400">
          {headers.length} columns · {total.toLocaleString()} saved rows
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="sr-only"
            onChange={(event) => {
              const file = event.currentTarget.files?.[0];
              event.currentTarget.value = "";
              if (file) void handleCsvFile(file);
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex h-7 items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 text-[12px] font-medium text-slate-700 transition hover:bg-slate-50 dark:border-border dark:bg-surface dark:text-slate-300 dark:hover:bg-surface-2"
          >
            <FileUp size={14} />
            Import CSV
          </button>
          <button
            type="button"
            onClick={addRow}
            className="inline-flex h-7 items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 text-[12px] font-medium text-slate-700 transition hover:bg-slate-50 dark:border-border dark:bg-surface dark:text-slate-300 dark:hover:bg-surface-2"
          >
            <Plus size={14} />
            Add row
          </button>
          <button
            type="button"
            onClick={addColumn}
            className="inline-flex h-7 items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 text-[12px] font-medium text-slate-700 transition hover:bg-slate-50 dark:border-border dark:bg-surface dark:text-slate-300 dark:hover:bg-surface-2"
          >
            <Plus size={14} />
            Add column
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saveState === "saving" || wouldTruncate}
            title={
              wouldTruncate
                ? "This sheet has more rows than are loaded here — saving would delete the rest. Edit large sheets via re-upload (Import CSV)."
                : "Save the source sheet"
            }
            className="inline-flex h-7 items-center gap-1.5 rounded-md bg-slate-900 px-2.5 text-[12px] font-medium text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-slate-100 dark:text-slate-950 dark:hover:bg-slate-200"
          >
            {saveState === "saving" ? (
              <LoaderCircle size={14} className="animate-spin" />
            ) : (
              <Save size={14} />
            )}
            Save source
          </button>
        </div>
      </div>

      {wouldTruncate && (
        <div
          className="shrink-0 border-b border-amber-200 bg-amber-50 px-3 py-1.5 text-[11.5px] text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-300"
          aria-live="polite"
        >
          Editing the first {loadedCount.toLocaleString()} of{" "}
          {total.toLocaleString()} rows. Saving is disabled because it would
          delete the remaining rows. To edit a sheet this large, replace it via
          Import CSV (this overwrites the whole sheet).
        </div>
      )}

      {message && (
        <div
          className={[
            "shrink-0 border-b px-3 py-1.5 text-[11.5px]",
            saveState === "error"
              ? "border-red-200 bg-red-50 text-red-700 dark:border-red-900/60 dark:bg-red-950/35 dark:text-red-300"
              : "border-slate-200 bg-slate-50 text-slate-700 dark:border-border dark:bg-surface-2 dark:text-slate-300",
          ].join(" ")}
          aria-live="polite"
        >
          {message}
        </div>
      )}

      {/* Grid */}
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="min-w-full border-collapse text-[13px]">
          <thead className="sticky top-0 z-10 bg-slate-100 dark:bg-surface-2">
            <tr>
              <th className="sticky left-0 z-20 w-14 border-b border-r border-slate-200 bg-slate-100 px-2 py-2 text-center text-[11px] font-medium text-slate-500 dark:border-border dark:bg-surface-2 dark:text-slate-400">
                #
              </th>
              {headers.map((header, columnIndex) => (
                <th
                  key={`${columnIndex}-${columnName(columnIndex)}`}
                  className="min-w-44 border-b border-r border-slate-200 p-0 align-top dark:border-border"
                >
                  <div className="flex h-10 items-center gap-1 px-2">
                    <button
                      type="button"
                      onClick={() => onInsertColumn?.(`/${header}`)}
                      title={
                        onInsertColumn
                          ? `Insert /${header}`
                          : columnName(columnIndex)
                      }
                      disabled={!onInsertColumn}
                      className="w-6 shrink-0 rounded text-[11px] font-semibold text-slate-500 transition hover:text-slate-900 disabled:cursor-default dark:text-slate-400 dark:hover:text-slate-100"
                    >
                      {columnName(columnIndex)}
                    </button>
                    <input
                      value={header}
                      onChange={(event) =>
                        setHeader(columnIndex, event.currentTarget.value)
                      }
                      className="h-8 min-w-0 flex-1 rounded border border-transparent bg-transparent px-2 text-[12px] font-semibold text-slate-800 outline-none transition focus:border-slate-300 focus:bg-white dark:text-slate-100 dark:focus:border-slate-600 dark:focus:bg-background"
                      aria-label={`Header ${columnName(columnIndex)}`}
                    />
                    <button
                      type="button"
                      onClick={() => deleteColumn(columnIndex)}
                      disabled={headers.length <= 1}
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-400 transition hover:bg-slate-200 hover:text-red-600 disabled:opacity-30 dark:hover:bg-background dark:hover:text-red-300"
                      aria-label={`Delete column ${columnName(columnIndex)}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className="border-b border-slate-100 dark:border-border"
              >
                <th className="sticky left-0 z-10 w-14 border-r border-slate-200 bg-slate-50 px-2 text-center align-middle text-[11px] font-medium text-slate-500 dark:border-border dark:bg-surface-2 dark:text-slate-400">
                  <div className="flex items-center justify-center gap-1">
                    <span>{rowIndex + 1}</span>
                    <button
                      type="button"
                      onClick={() => deleteRow(rowIndex)}
                      className="flex h-6 w-6 items-center justify-center rounded text-slate-400 transition hover:bg-slate-200 hover:text-red-600 dark:hover:bg-background dark:hover:text-red-300"
                      aria-label={`Delete row ${rowIndex + 1}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </th>
                {headers.map((_, columnIndex) => (
                  <td
                    key={`${rowIndex}-${columnIndex}`}
                    className="h-10 min-w-44 border-r border-slate-100 p-0 dark:border-border"
                  >
                    <input
                      value={cellInputValue(row[columnIndex] ?? null)}
                      onChange={(event) =>
                        setCell(
                          rowIndex,
                          columnIndex,
                          event.currentTarget.value,
                        )
                      }
                      className="h-10 w-full min-w-44 bg-transparent px-3 text-[13px] text-slate-900 outline-none transition focus:bg-slate-50 dark:text-slate-100 dark:focus:bg-background"
                      aria-label={`${columnName(columnIndex)}${rowIndex + 1}`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
